// «Registrar reintegro» de un cobro duplicado APLICADO (dos pagos `succeeded`
// del mismo periodo, migracion 0053).
//
// A diferencia de «Marcar como reintegrado» (intento que no se acredito), aca
// el backend SI mueve el registro contable: revierte el diferido, pasa el pago
// a `refunded`, deja el ledger y el audit, y emite la Nota de Credito si la
// factura estaba autorizada. La plata se devuelve en la pasarela: este boton
// asienta que ya se hizo. Exige Usuarios en nivel completo + danger_zone.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { adminRefundAppliedDuplicate, AdminApiError } from '@/lib/api';
import { money } from '@/lib/format';
import { REFUND_REFERENCE_MAX, refundReferenceProblem, shortPaymentId } from '@/lib/reconcile';
import { canMarkRefunded } from '@/lib/access';
import { useSession } from '@/hooks/useSession';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls } from '@/lib/form-styles';

export default function RefundAppliedDuplicateButton({
  userId,
  paymentId,
  amount,
  currency,
  customer,
  pairedPaymentId,
}: {
  userId: string;
  paymentId: string;
  amount: string | number;
  currency: string;
  customer?: string | null;
  pairedPaymentId: string;
}): JSX.Element {
  const session = useSession();
  const allowed = canMarkRefunded(session);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [touched, setTouched] = useState(false);

  const problem = refundReferenceProblem(reference);
  const mut = useMutation({
    mutationFn: () => adminRefundAppliedDuplicate(userId, paymentId, reference.trim()),
    onSuccess: () => {
      setOpen(false);
      setReference('');
      setTouched(false);
      void qc.invalidateQueries({ queryKey: ['admin-pending-charges'] });
      void qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
  });

  return (
    <>
      <button
        type="button"
        data-help="charges.refund-applied-duplicate"
        onClick={() => {
          mut.reset();
          setOpen(true);
        }}
        disabled={!allowed}
        title={
          allowed
            ? undefined
            : 'Requiere Usuarios en nivel completo y el permiso danger_zone'
        }
        className="min-h-[2rem] px-2.5 text-xs inline-flex items-center gap-1.5 rounded-lg font-medium border border-status-error/40 text-status-error hover:bg-status-error/10 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-status-error"
      >
        <Undo2 size={14} aria-hidden="true" />
        Registrar reintegro
      </button>

      <ConfirmDialog
        open={open}
        title="Registrar reintegro del cobro duplicado"
        description={
          <div className="space-y-3">
            <p>
              El pago{' '}
              <span className="font-mono text-ink dark:text-ink-inverted" title={paymentId}>
                {shortPaymentId(paymentId)}
              </span>{' '}
              de <strong className="text-ink dark:text-ink-inverted">{money(amount, currency)}</strong>
              {customer ? ` de ${customer}` : ''} cubre el mismo período que el pago{' '}
              <span className="font-mono" title={pairedPaymentId}>
                {shortPaymentId(pairedPaymentId)}
              </span>
              , que sostiene el plan.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>
                <strong>Confirmá antes que el reintegro se hizo en la pasarela.</strong> Esto no
                devuelve la plata: la asienta.
              </li>
              <li>
                Se revierte el ingreso del cobro (diferido/devengado), el pago pasa a «reintegrado»
                y el plan del cliente <strong>no cambia</strong>.
              </li>
              <li>Si la factura estaba autorizada, se emite la Nota de Crédito.</li>
              <li>No se puede deshacer desde acá.</li>
            </ul>
            <label className="block text-xs">
              <span className="font-medium">
                Referencia del reintegro (n.º de operación o comentario)
              </span>
              <input
                type="text"
                value={reference}
                maxLength={REFUND_REFERENCE_MAX}
                onChange={(e) => setReference(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="Ej.: operación 778899 de la pasarela"
                aria-invalid={touched && problem ? true : undefined}
                className={fieldCls({ extra: 'mt-1', invalid: Boolean(touched && problem) })}
              />
            </label>
            {touched && problem ? <p className="text-status-error text-xs">{problem}</p> : null}
            {mut.isError ? (
              <p className="text-status-error text-xs">
                Error: {mut.error instanceof AdminApiError ? mut.error.message : 'desconocido'}
              </p>
            ) : null}
            {mut.isSuccess ? (
              <p className="text-xs">
                Reintegro asentado.
                {mut.data.credit_note
                  ? ` Nota de crédito: ${mut.data.credit_note.status}${
                      mut.data.credit_note.full_number ? ` ${mut.data.credit_note.full_number}` : ''
                    }.`
                  : ' Sin factura autorizada: no hacía falta Nota de Crédito.'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Registrar reintegro"
        loading={mut.isPending}
        onConfirm={() => {
          setTouched(true);
          if (problem) return;
          mut.mutate();
        }}
        onCancel={() => {
          setOpen(false);
          mut.reset();
        }}
      />
    </>
  );
}
